import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { DatePicker } from './date-picker';
import { TimePicker } from './time-picker';

/**
 * Chọn **một mốc thời gian** (ngày + giờ) — thay cho `<input type="datetime-local">`.
 *
 * Ghép hai ô đã có thay vì dựng một widget thứ ba: người dùng đã học cách dùng
 * `<DatePicker>` và `<TimePicker>` ở mọi biểu mẫu khác, và một hộp thoại
 * ngày-giờ riêng sẽ là thứ duy nhất trong ứng dụng cư xử khác chúng.
 *
 * ## ⚠️ Vào/ra là ISO có `Z`, nhưng người dùng thấy GIỜ ĐỊA PHƯƠNG
 *
 * Đây là chỗ khác hẳn `<DatePicker>`. `CalendarDate` là một ngày trên lịch,
 * không múi giờ — còn mốc của Replay là một **thời điểm thật**, và "lúc 3 giờ
 * chiều" phải nghĩa là 3 giờ chiều nơi người đang xem ngồi. Nên chỗ này cố ý
 * dùng `getFullYear`/`getHours` (giờ địa phương) chứ không phải các hàm UTC của
 * `lib/date.ts`.
 *
 * Nhầm hai mô hình này với nhau là lỗi lệch nửa ngày trên thanh trượt Replay,
 * và nó chỉ lộ ra với người ở múi giờ khác múi giờ của người viết code.
 */
export interface DateTimePickerProps {
  /** ISO đầy đủ, ví dụ `2026-09-04T08:30:00.000Z`. */
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
}

export function DateTimePicker({
  value,
  onChange,
  disabled,
  className,
  ...aria
}: DateTimePickerProps): ReactNode {
  const current = new Date(value);
  const valid = !Number.isNaN(current.getTime());

  const datePart = valid ? localDate(current) : '';
  const timePart = valid ? localTime(current) : '';

  const emit = (date: string, time: string): void => {
    if (date === '') return;
    const [hour, minute] = (time === '' ? '00:00' : time).split(':');
    const next = new Date(`${date}T00:00:00`);
    next.setHours(Number(hour), Number(minute), 0, 0);
    onChange(next.toISOString());
  };

  return (
    <div className={cn('flex items-center gap-1.5', className)} {...aria}>
      <DatePicker
        className="w-36"
        value={datePart}
        disabled={disabled}
        clearable={false}
        onChange={(next) => emit(next, timePart)}
      />
      <TimePicker
        className="w-24"
        value={timePart}
        disabled={disabled}
        step={15}
        onChange={(next) => emit(datePart, next)}
      />
    </div>
  );
}

/** `Date` → `YYYY-MM-DD` theo **giờ địa phương** — xem chú thích ở đầu file. */
function localDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function localTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
